import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import API from '../services/config/api';
import '../styles/portal-auth.scss';
import PasswordEyeToggle from '../Components/PasswordEyeToggle';

function RiskPortalResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [form, setForm] = useState({ password: '', confirm: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async () => {
        if (!form.password || !form.confirm) { setError('Both fields are required.'); return; }
        if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
        if (!token) { setError('Invalid or missing reset token. Please request a new reset link.'); return; }
        setLoading(true); setError('');
        try {
            const axios = (await import('axios')).default;
            await axios.post(
                API.RISK_PORTAL_RESET_PASSWORD,
                { token, password: form.password, confirm_password: form.confirm },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                        'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2',
                    },
                }
            );
            setSuccess(true);
            setTimeout(() => navigate('/risk-portal/login'), 3000);
        } catch (e) {
            const msg = e?.response?.data?.response_data?.message || e?.response?.data?.error?.message || '';
            setError(msg || 'Failed to reset password. The link may have expired.');
        }
        setLoading(false);
    };

    const features = [
        'End-to-end risk lifecycle management',
        'Severity-tiered risk acceptance workflow',
        'KRI tracking & treatment monitoring',
        'Role-based collaboration & sign-offs',
    ];

    return (
        <div className="portal-auth-wrapper">
            {/* Left branding panel */}
            <div className="portal-left-panel">
                <div className="portal-left-content">
                    <p className="portal-title-main">Risk</p>
                    <p className="portal-title-accent">Portal</p>
                    <p className="portal-tagline">
                        Centralised risk management, treatment tracking, and real-time reporting — all in one place.
                    </p>
                    <div className="portal-features">
                        {features.map((text, i) => (
                            <div key={i} className="portal-feature-item">
                                <span className="portal-check">✓</span>
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right form panel */}
            <div className="portal-form-panel">
                <div className="portal-form-wrapper">
                    <div className="portal-logo-wrap">
                        <img src="/assets/small_logo.svg" alt="NMT" width={300} height={90} onError={e => { e.target.style.display = 'none'; }} />
                    </div>

                    <div className="portal-card">
                        <h1 className="portal-card-title">Reset Password</h1>
                        <p className="portal-card-subtitle">Enter your new password below.</p>

                        {error && <div className="portal-alert portal-alert--error">{error}</div>}

                        {success ? (
                            <>
                                <div className="portal-alert portal-alert--success">
                                    ✓ &nbsp;Password reset successfully. Redirecting to login...
                                </div>
                                <Link to="/risk-portal/login" className="portal-back-link">Go to Login</Link>
                            </>
                        ) : (
                            <>
                                {!token && (
                                    <div className="portal-alert portal-alert--warning">
                                        ⚠ No reset token found. Please use the link from your email.
                                    </div>
                                )}

                                <div className="portal-form-group">
                                    <label htmlFor="rp-new-pw">New Password</label>
                                    <input
                                        id="rp-new-pw"
                                        type={showPassword ? 'text' : 'password'}
                                        className="portal-input portal-input--with-icon"
                                        value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                                        placeholder="Min. 8 characters"
                                    />
                                    <PasswordEyeToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} className="portal-eye-btn" />
                                </div>

                                <div className="portal-form-group">
                                    <label htmlFor="rp-confirm-pw">Confirm Password</label>
                                    <input
                                        id="rp-confirm-pw"
                                        type={showConfirm ? 'text' : 'password'}
                                        className="portal-input portal-input--with-icon"
                                        value={form.confirm}
                                        onChange={e => setForm({ ...form, confirm: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                                        placeholder="Re-enter password"
                                    />
                                    <PasswordEyeToggle visible={showConfirm} onClick={() => setShowConfirm(v => !v)} className="portal-eye-btn" />
                                    {form.confirm && form.password !== form.confirm && (
                                        <p className="portal-match-error">Passwords do not match.</p>
                                    )}
                                </div>

                                <button
                                    className="portal-submit-btn"
                                    onClick={handleSubmit}
                                    disabled={loading || !token}
                                    style={!token ? { background: '#94a3b8' } : {}}
                                >
                                    {loading ? 'Resetting...' : 'Reset Password'}
                                </button>

                                <Link to="/risk-portal/login" className="portal-back-link">← Back to Login</Link>
                            </>
                        )}

                        <div className="portal-card-footer">
                            NMT Security &nbsp;·&nbsp; Risk Portal
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RiskPortalResetPassword;
